const { cleanCartLine } = require('./cartLineCleaning');
const {
    cloneDeep,
    cloneShallow,
} = require('../../../functions/clone');

/**
 * @constructor
 *
 * @param {CartLineNumberFactory} cartLineNumberFactory
 */
const CartLineSplitter = function (
    cartLineNumberFactory
) {
    /**
     * @type {CartLineNumberFactory}
     */
    this.cartLineNumberFactory = cartLineNumberFactory;
};

CartLineSplitter.prototype = {
    /**
     * Get all the lines from the cart split up where necessary, ready to send to the API.
     *
     * @param {Cart} cart
     *
     * @return {CartLine[]}
     */
    splitCartLinesForApi(cart) {
        return this.splitDiscounts(cart.itemList);
    },

    /**
     * If lines have multiple different discounts, or only 1 discount but it does not apply to the
     * entire line, split those into separate lines.
     *
     * @param {CartLine[]} items
     *
     * @return {CartLine[]}
     */
    splitDiscounts(items) {
        let newItems = [];

        for (let i = 0; i < items.length; i++) {
            // Clone this line so we don't modify the original.
            /** @var {CartLine} */
            const item = cloneShallow(items[i]);
            item.itemInstances = cloneDeep(item.itemInstances);

            // Remove any inactive discounts.
            item.discounts = item.discounts.filter((discount) => discount.isActive());

            /**
             * The item instances from the original line.
             * Copied to a new array here so we can remove from it without other stuff interfering and adding
             * instances back in / removing them.
             * At the end this becomes the list of instances for the original line.
             *
             * @type {ItemInstance[]}
             */
            const originalLineItemInstances = [...item.itemInstances];

            // Run calculate on the line so we're sure the discountedQuantity property is up to date.
            item.calculate(false);

            const isRefund = item.quantity < 0;

            // We only need to do anything with line if it has multiple discounts, or if there is spare qty
            // on the line that is not discounted.
            // We want to end up with a separate line for each individual discount (or lack of discount).
            if (
                // Multiple discounts
                item.discounts.length > 1
                // or not all the qty is discounted.
                || (Math.abs(item.discountedQuantity) && Math.abs(item.discountedQuantity) < Math.abs(item.quantity))
            ) {
                for (let di = 0; di < item.discounts.length; di++) {
                    /**
                     * @type {CartItemDiscount}
                     */
                    let itemDiscount = item.discounts[di];
                    if (!itemDiscount.isActive()) {
                        continue;
                    }

                    // Create a separate line for this discounted portion of the line.
                    let discountedLine = item.clone();
                    if (isRefund) {
                        discountedLine.setQuantity(-1 * itemDiscount.qty);
                    } else {
                        discountedLine.setQuantity(itemDiscount.qty);

                        // Steal this number of item instances from the original line.
                        // First reset the instances on the new line as they will currently be a clone of the original
                        // line's instances.
                        discountedLine.itemInstances = [];
                        for (let newInstance = 1; newInstance <= itemDiscount.qty; newInstance++) {
                            // Check that there is actually an instance available.
                            // In the case of normal discounts and offers we shouldn't need to check because there can
                            // only be 1 active offer or discount per line (and we have already removed inactive
                            // discounts when we cloned the item above) BUT a variable discount can apply in addition to
                            // an order level discount on the same line.
                            if (originalLineItemInstances.length) {
                                discountedLine.itemInstances.push(
                                    originalLineItemInstances.shift().setInstance(newInstance)
                                );
                            }
                        }
                    }

                    discountedLine.clearDiscounts();
                    discountedLine.setDiscounts([itemDiscount]);
                    discountedLine.calculate();

                    // Remove originalItemsID from the line to prevent sending multiple lines to the API
                    // with the same ordersItemsID.
                    discountedLine.ordersItemsID = null;
                    discountedLine.qtyBeforeAdjustment = 0;

                    // The new line needs a new line number
                    discountedLine.lineNumber = this.cartLineNumberFactory.next();

                    // Remove this discounted qty from the original line
                    item.adjustQuantity(-discountedLine.getQuantity(), false);

                    // Add the newly split line
                    newItems.push(discountedLine);
                }

                // Now all the discounted parts of the line have been split off into new lines.
                // We might still have remaining qty on the original line that is not discounted (which may well
                // be the entire line, if there were no discounts on the line)

                // Use the remaining instances for the original line.
                item.itemInstances = originalLineItemInstances.map(
                    (ii, index) => ii.setInstance(index + 1)
                );

                // Remove discounts from the original line as they have now been split out
                item.clearActiveDiscounts();
                item.calculate();

                // Add the remaining non discounted portion of the line
                if (item.getQuantity() !== 0) {
                    newItems.push(item);
                }
            } else {
                // We don't need to split the line so we can push it as is.
                newItems.push(item);
            }
        }

        newItems.forEach(cleanCartLine);

        return newItems;
    },
};

module.exports = CartLineSplitter;
